import { log } from '@common/core-layer/services/logger.service'

import {
  ATTACHMENT_MAX_FILE_SIZE,
  VOICE_NOTE_ACCEPTED_AUDIO_TYPES,
  VOICE_NOTE_MAX_DURATION_SEC,
  type VoiceNoteAcceptedAudioType,
} from './constants'
import type {
  PrepareUploadedVoiceNoteOptions,
  PrepareUploadedVoiceNoteResult,
  PreparedVoiceNote,
  VoicePrepareFailureCode,
} from '../types/voice-note.types'
import type { VoiceRecordingResult } from '../types/voice.types'

const EXT_BY_MIME_TOKEN: Array<[token: string, ext: string]> = [
  ['wav', 'wav'],
  ['mp4', 'm4a'],
  ['mpeg', 'mp3'],
  ['ogg', 'ogg'],
  ['webm', 'webm'],
]

const PROBE_TIMEOUT_MS = 4000

function isPositiveFinite(n: number): boolean {
  return Number.isFinite(n) && n > 0
}

/** Upload gate: duration must be known and ≤ max. */
export function isVoiceNoteUploadDurationAllowed(durationSec: number): boolean {
  return isPositiveFinite(durationSec) && durationSec <= VOICE_NOTE_MAX_DURATION_SEC
}

/** Read duration via HTMLAudioElement metadata; `0` when unavailable. */
function probeAudioFileDurationSec(file: File): Promise<number> {
  if (typeof Audio === 'undefined' || typeof URL === 'undefined') return Promise.resolve(0)

  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file)
    const audio = new Audio()
    let settled = false

    const finish = (durationSec: number) => {
      if (settled) return
      settled = true
      audio.removeAttribute('src')
      try {
        audio.load()
      } catch (error) {
        log.warn('Audio duration probe teardown failed', {
          error: error instanceof Error ? error : undefined,
        })
      }
      URL.revokeObjectURL(objectUrl)
      resolve(isPositiveFinite(durationSec) ? durationSec : 0)
    }

    audio.preload = 'metadata'
    audio.onloadedmetadata = () => finish(audio.duration)
    audio.onerror = () => finish(0)
    globalThis.setTimeout?.(() => finish(0), PROBE_TIMEOUT_MS)
    audio.src = objectUrl
  })
}

/** True when the file MIME is in the picker allowlist used by drop and upload. */
export function isVoiceNoteAudioFile(file: Pick<File, 'type'>): boolean {
  const normalizedType = (file.type || '').toLowerCase()
  return VOICE_NOTE_ACCEPTED_AUDIO_TYPES.includes(normalizedType as VoiceNoteAcceptedAudioType)
}

/** Map recorder/upload MIME to a file extension for signed-URL uploads. */
function voiceFileExtension(mimeType: string): string {
  const lower = mimeType.toLowerCase()
  return EXT_BY_MIME_TOKEN.find(([token]) => lower.includes(token))?.[1] ?? 'wav'
}

/** Stop a recorder and map the result into a prepared note (or null). */
export async function capturePreparedVoiceNote(
  stop: () => Promise<Pick<VoiceRecordingResult, 'blob' | 'mimeType' | 'durationSec' | 'waveformPeaks'> | null>
): Promise<PreparedVoiceNote | null> {
  const result = await stop()
  if (!result) return null

  const mimeType = result.mimeType || 'audio/wav'
  if (result.blob.size > ATTACHMENT_MAX_FILE_SIZE) return null
  const file = new File([result.blob], `voice-note-${Date.now()}.${voiceFileExtension(mimeType)}`, { type: mimeType })
  return {
    file,
    localUrl: URL.createObjectURL(result.blob),
    mimeType,
    durationSec: result.durationSec,
    waveformPeaks: result.waveformPeaks,
    kind: 'voice_note',
    source: 'recorded',
    fileName: file.name,
    size: file.size,
  }
}

/**
 * Validate + analyze a picked/dropped audio file into a prepared note.
 * Uploaded attachments use timeline chrome — skip waveform decode/storage.
 */
export async function prepareUploadedVoiceNote(
  file: File,
  options: PrepareUploadedVoiceNoteOptions = {}
): Promise<PrepareUploadedVoiceNoteResult> {
  const fail = (code: VoicePrepareFailureCode): PrepareUploadedVoiceNoteResult => {
    const localUrl = options.localUrl
    if (localUrl?.startsWith('blob:')) URL.revokeObjectURL(localUrl)
    return { ok: false, code }
  }

  if (file.size > (options.maxFileSize ?? ATTACHMENT_MAX_FILE_SIZE)) return fail('too_large')
  if (!isVoiceNoteAudioFile(file)) return fail('unsupported')

  let durationSec: number
  try {
    durationSec = await probeAudioFileDurationSec(file)
    if (!durationSec) return fail('failed')
    if (!isVoiceNoteUploadDurationAllowed(durationSec)) return fail('too_long')
  } catch (error) {
    log.warn('Audio duration probe failed', {
      error: error instanceof Error ? error : undefined,
      fileType: file.type,
      fileSize: file.size,
    })
    return fail('failed')
  }

  const mimeType = file.type || 'audio/mpeg'
  return {
    ok: true,
    note: {
      file,
      localUrl: options.localUrl || URL.createObjectURL(file),
      mimeType,
      durationSec,
      waveformPeaks: [],
      kind: 'attachment',
      source: 'uploaded',
      fileName: file.name || `voice-note-${Date.now()}.${voiceFileExtension(mimeType)}`,
      size: file.size,
    },
  }
}
